import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";
import { formatApiError } from "@/lib/api";
import { ShieldCheck, Globe2 } from "lucide-react";

export const ROLE_CATEGORIES = [
  "Security Guard", "CPO / VPO", "Door Security", "Event Security",
  "Static Security", "Asset Protection", "Security Driver",
  "Medical Support", "Team Lead", "Supervisor",
];

export const KIT_ITEMS = [
  "PSA licence", "SIA licence", "CPO qualification", "First aid certificate", "Medical qualification",
  "Radio / comms equipment", "Earpiece", "Hi-vis", "Branded uniform",
  "Suit / formal dress", "Vehicle access", "Clean driving licence", "Dashcam",
  "Torch", "Notebook / incident log", "Smartphone", "Power bank", "PPE", "Site-specific equipment",
];

export const IE_COUNTIES = [
  "Dublin", "Cork", "Galway", "Limerick", "Waterford", "Kilkenny", "Wexford",
  "Kerry", "Meath", "Kildare", "Wicklow", "Louth", "Donegal", "Mayo", "Sligo",
  "Clare", "Tipperary", "Offaly", "Laois", "Cavan", "Monaghan", "Longford",
  "Roscommon", "Leitrim", "Westmeath", "Carlow",
];

export const UK_REGIONS = [
  "London", "Greater Manchester", "West Midlands (Birmingham)", "Merseyside (Liverpool)",
  "West Yorkshire (Leeds)", "South Yorkshire (Sheffield)", "Tyne and Wear (Newcastle)",
  "Bristol", "Nottingham", "Leicester", "Cardiff", "Belfast", "Glasgow", "Edinburgh",
  "Aberdeen", "Southampton", "Portsmouth", "Brighton", "Milton Keynes", "Northampton",
  "Coventry", "Reading", "Oxford", "Cambridge", "Norwich", "Plymouth", "Other UK",
];

export default function RegisterStaff() {
  const { registerStaff } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [f, setF] = useState({
    email: "",
    password: "",
    full_name: "",
    phone: "",
    country_of_residence: "IE",
    countries_available: ["IE"],
    right_to_work_ie: true,
    right_to_work_uk: false,
    willing_to_travel: false,
    willing_cross_border: false,
    county: "",
    role_categories: [],
    years_experience: 0,
    psa_licence_number: "",
    psa_licence_expiry: "",
    sia_licence_number: "",
    sia_licence_expiry: "",
    cp_qualification: false,
    first_aid_qualification: false,
    other_certifications: "",
    driving_licence: false,
    vehicle_access: false,
    languages: "",
    previous_roles: "",
    specialist_skills: "",
    emergency_contact: "",
    kit: [],
    availability: "",
    agreement_accepted: false,
    english_level: "",
    referral_code: "",
    references: [{ company_name: "", company_website: "", referee_name: "", referee_email: "", referee_phone: "", role_held: "", period: "", context: "" }],
  });

  useEffect(() => {
    const ref = params.get("ref");
    if (ref) setF((cur) => ({ ...cur, referral_code: ref.toUpperCase() }));
  }, [params]);

  const toggle = (arr, key, val) => {
    const s = new Set(arr);
    if (s.has(val)) s.delete(val); else s.add(val);
    setF({ ...f, [key]: Array.from(s) });
  };

  const toggleCountry = (code) => {
    const s = new Set(f.countries_available);
    if (s.has(code)) s.delete(code); else s.add(code);
    setF({ ...f, countries_available: Array.from(s) });
  };

  const locations = f.country_of_residence === "UK" ? UK_REGIONS : IE_COUNTIES;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.agreement_accepted) return setError("You must agree to the platform terms.");
    if (f.role_categories.length === 0) return setError("Please select at least one role category.");
    if (f.countries_available.length === 0) return setError("Select at least one country you are available to work in.");
    if (!f.right_to_work_ie && !f.right_to_work_uk) return setError("You must confirm right to work in Ireland or the UK.");
    if (Number(f.years_experience) < 2) return setError("SecuSource requires a minimum of 2 years of verifiable security experience. This standard protects clients and colleagues.");
    const ENGLISH_LEVELS = ["A1","A2","B1","B2","C1","C2"];
    if (!f.english_level || !ENGLISH_LEVELS.includes(f.english_level)) return setError("Please select your English level (CEFR).");
    if (ENGLISH_LEVELS.indexOf(f.english_level) < ENGLISH_LEVELS.indexOf("B2")) return setError("SecuSource requires a minimum English level of B2 (CEFR). This ensures pros can read SOPs, briefings and communicate on-site.");
    const validRefs = (f.references || []).filter(r => r.company_name && r.referee_name && r.referee_email);
    if (validRefs.length === 0) return setError("At least one previous-employer reference is required. SecuSource emails each referee to confirm your reliability.");
    setBusy(true);
    try {
      const payload = {
        ...f,
        years_experience: Number(f.years_experience),
        languages: f.languages.split(",").map((s) => s.trim()).filter(Boolean),
        references: (f.references || []).filter(r => r.company_name && r.referee_name && r.referee_email),
      };
      await registerStaff(payload);
      nav("/staff");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <div className="text-center mb-6">
        <div className="inline-flex w-14 h-14 rounded-md bg-brand items-center justify-center mb-4">
          <ShieldCheck className="w-7 h-7 text-white" strokeWidth={2.4} />
        </div>
        <h1 className="text-3xl font-outfit font-bold text-white">Join SecuSource</h1>
        <p className="text-white/60 text-sm mt-1 font-manrope">Free to join. Built by security &amp; CPOs for the industry. Ireland &amp; UK.</p>
        <div className="mt-3 inline-flex items-center gap-2 bg-gold-50 text-gold-600 px-3 py-1 rounded-full text-xs font-outfit font-bold uppercase tracking-widest">
          <ShieldCheck className="w-3.5 h-3.5" /> €0 to join · You negotiate your rate
        </div>
      </div>

      <form onSubmit={submit} className="card-flat p-6 space-y-6">
        {/* Account */}
        <section>
          <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-3">Account</div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Email</Label><Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="reg-staff-email" /></div>
            <div><Label>Password</Label><Input required type="password" minLength={6} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="reg-staff-password" /></div>
          </div>
        </section>

        {/* Identity */}
        <section>
          <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-3">Personal Details</div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Full name</Label><Input required value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="reg-staff-name" /></div>
            <div><Label>Phone</Label><Input required value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="reg-staff-phone" /></div>
            <div><Label>Years of experience <span className="text-red-600" title="Minimum 2 years required">*</span></Label><Input type="number" min={2} value={f.years_experience} onChange={(e) => setF({ ...f, years_experience: e.target.value })} data-testid="reg-staff-exp" /><div className="text-[11px] text-white/60 font-manrope mt-1">Minimum 2 years of verifiable security experience required.</div></div>
            <div>
              <Label>English level (CEFR) <span className="text-red-600">*</span></Label>
              <Select value={f.english_level} onValueChange={(v) => setF({ ...f, english_level: v })}>
                <SelectTrigger data-testid="reg-staff-english"><SelectValue placeholder="Select level" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="A1">A1 — Beginner</SelectItem>
                  <SelectItem value="A2">A2 — Elementary</SelectItem>
                  <SelectItem value="B1">B1 — Intermediate</SelectItem>
                  <SelectItem value="B2">B2 — Upper-intermediate (minimum)</SelectItem>
                  <SelectItem value="C1">C1 — Advanced</SelectItem>
                  <SelectItem value="C2">C2 — Proficient / Native</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-[11px] text-white/60 font-manrope mt-1">Minimum B2 required. Read SOPs, briefings and communicate on-site.</div>
            </div>
            <div><Label>Languages (comma-separated)</Label><Input placeholder="English, Irish" value={f.languages} onChange={(e) => setF({ ...f, languages: e.target.value })} data-testid="reg-staff-langs" /></div>
            <div className="md:col-span-2"><Label>Emergency contact</Label><Input value={f.emergency_contact} onChange={(e) => setF({ ...f, emergency_contact: e.target.value })} data-testid="reg-staff-emergency" /></div>
          </div>
        </section>

        {/* Country & right to work */}
        <section className="rounded-lg border border-blue-100 bg-blue-50/40 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Globe2 className="w-4 h-4 text-brand" />
            <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">Country &amp; Right to Work</div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Country of residence</Label>
              <Select value={f.country_of_residence} onValueChange={(v) => setF({ ...f, country_of_residence: v, county: "" })}>
                <SelectTrigger data-testid="reg-staff-country-residence"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="IE">Ireland</SelectItem>
                  <SelectItem value="UK">United Kingdom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{f.country_of_residence === "UK" ? "UK region" : "Irish county"}</Label>
              <Select value={f.county} onValueChange={(v) => setF({ ...f, county: v })}>
                <SelectTrigger data-testid="reg-staff-county"><SelectValue placeholder={`Choose a ${f.country_of_residence === "UK" ? "region" : "county"}`} /></SelectTrigger>
                <SelectContent>{locations.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3">
            <Label className="mb-2 block">Countries you are available to work in</Label>
            <div className="flex gap-4 text-sm font-manrope">
              <label className="flex items-center gap-2"><Checkbox checked={f.countries_available.includes("IE")} onCheckedChange={() => toggleCountry("IE")} data-testid="reg-staff-avail-ie" /> Ireland</label>
              <label className="flex items-center gap-2"><Checkbox checked={f.countries_available.includes("UK")} onCheckedChange={() => toggleCountry("UK")} data-testid="reg-staff-avail-uk" /> United Kingdom</label>
            </div>
          </div>
          <div className="mt-3 grid md:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm font-manrope"><Checkbox checked={f.right_to_work_ie} onCheckedChange={(v) => setF({ ...f, right_to_work_ie: !!v })} data-testid="reg-staff-rtw-ie" /> I have the right to work in Ireland</label>
            <label className="flex items-center gap-2 text-sm font-manrope"><Checkbox checked={f.right_to_work_uk} onCheckedChange={(v) => setF({ ...f, right_to_work_uk: !!v })} data-testid="reg-staff-rtw-uk" /> I have the right to work in the UK</label>
            <label className="flex items-center gap-2 text-sm font-manrope"><Checkbox checked={f.willing_to_travel} onCheckedChange={(v) => setF({ ...f, willing_to_travel: !!v })} data-testid="reg-staff-travel" /> Willing to travel within country</label>
            <label className="flex items-center gap-2 text-sm font-manrope"><Checkbox checked={f.willing_cross_border} onCheckedChange={(v) => setF({ ...f, willing_cross_border: !!v })} data-testid="reg-staff-cross-border" /> Willing to work cross-border (IE ↔ UK)</label>
          </div>
        </section>

        {/* Role categories */}
        <section>
          <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-3">Role Categories (select all that apply)</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {ROLE_CATEGORIES.map((r) => (
              <label key={r} className="flex items-center gap-2 text-sm font-manrope cursor-pointer">
                <Checkbox checked={f.role_categories.includes(r)} onCheckedChange={() => toggle(f.role_categories, "role_categories", r)} data-testid={`reg-role-${r.replace(/\W+/g, "-")}`} />
                <span>{r}</span>
              </label>
            ))}
          </div>
        </section>

        {/* Licences & Qualifications */}
        <section>
          <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-3">Licences &amp; Qualifications</div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>PSA licence number (Ireland)</Label><Input value={f.psa_licence_number} onChange={(e) => setF({ ...f, psa_licence_number: e.target.value })} data-testid="reg-staff-psa" /></div>
            <div><Label>PSA expiry date</Label><Input type="date" value={f.psa_licence_expiry} onChange={(e) => setF({ ...f, psa_licence_expiry: e.target.value })} data-testid="reg-staff-psa-exp" /></div>
            <div><Label>SIA licence number (UK)</Label><Input value={f.sia_licence_number} onChange={(e) => setF({ ...f, sia_licence_number: e.target.value })} data-testid="reg-staff-sia" /></div>
            <div><Label>SIA expiry date</Label><Input type="date" value={f.sia_licence_expiry} onChange={(e) => setF({ ...f, sia_licence_expiry: e.target.value })} data-testid="reg-staff-sia-exp" /></div>
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-sm font-manrope">
            <label className="flex items-center gap-2"><Checkbox checked={f.cp_qualification} onCheckedChange={(v) => setF({ ...f, cp_qualification: !!v })} data-testid="reg-staff-cp" /> Close Protection qualified</label>
            <label className="flex items-center gap-2"><Checkbox checked={f.first_aid_qualification} onCheckedChange={(v) => setF({ ...f, first_aid_qualification: !!v })} data-testid="reg-staff-firstaid" /> First Aid certified</label>
          </div>
          <div className="mt-3">
            <Label>Other security certifications</Label>
            <Textarea rows={2} value={f.other_certifications} onChange={(e) => setF({ ...f, other_certifications: e.target.value })} placeholder="e.g., FPOS, MIRA, PALS, dog handling, tactical medic…" data-testid="reg-staff-other-certs" />
          </div>
        </section>

        {/* Vehicle */}
        <section>
          <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-3">Vehicle &amp; Driving</div>
          <div className="flex flex-wrap gap-4 text-sm font-manrope">
            <label className="flex items-center gap-2">
              <Checkbox checked={f.driving_licence} onCheckedChange={(v) => setF({ ...f, driving_licence: !!v })} data-testid="reg-staff-driving" /> Driving licence
            </label>
            <label className="flex items-center gap-2">
              <Checkbox checked={f.vehicle_access} onCheckedChange={(v) => setF({ ...f, vehicle_access: !!v })} data-testid="reg-staff-vehicle" /> Vehicle access
            </label>
          </div>
        </section>

        {/* Kit */}
        <section>
          <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-3">Kit Checklist</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {KIT_ITEMS.map((k) => (
              <label key={k} className="flex items-center gap-2 text-sm font-manrope cursor-pointer">
                <Checkbox checked={f.kit.includes(k)} onCheckedChange={() => toggle(f.kit, "kit", k)} data-testid={`reg-kit-${k.replace(/\W+/g, "-")}`} />
                <span>{k}</span>
              </label>
            ))}
          </div>
        </section>

        {/* Free text */}
        <section>
          <div className="grid gap-4">
            <div><Label>Previous roles</Label><Textarea rows={3} value={f.previous_roles} onChange={(e) => setF({ ...f, previous_roles: e.target.value })} data-testid="reg-staff-prev-roles" /></div>
            <div><Label>Specialist skills</Label><Textarea rows={3} value={f.specialist_skills} onChange={(e) => setF({ ...f, specialist_skills: e.target.value })} data-testid="reg-staff-skills" /></div>
            <div><Label>Availability</Label><Textarea rows={2} value={f.availability} onChange={(e) => setF({ ...f, availability: e.target.value })} data-testid="reg-staff-availability" placeholder="e.g., Weekends, nights, 24/7 on call" /></div>
          </div>
        </section>

        {/* Professional references */}
        <section className="rounded-lg border border-amber-100 bg-amber-50/40 p-4" data-testid="references-section">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-4 h-4 text-amber-600" />
            <div className="text-xs uppercase tracking-widest text-amber-700 font-outfit font-bold">Professional References <span className="text-red-600">*</span></div>
          </div>
          <p className="text-sm text-white/70 font-manrope mb-3">
            List at least one previous employer as a professional reference. SecuSource emails each referee directly to confirm your reliability, timekeeping, conduct and integrity. Their reply is private to SecuSource admin.
          </p>

          {f.references.map((r, idx) => (
            <div key={idx} className="bg-[#0A0A0A] rounded-md border border-amber-100 p-4 mb-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">Reference {idx + 1}</div>
                {f.references.length > 1 && (
                  <button type="button" onClick={() => setF({ ...f, references: f.references.filter((_, i) => i !== idx) })} className="text-xs text-red-600 font-manrope font-semibold" data-testid={`ref-remove-${idx}`}>Remove</button>
                )}
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>Previous employer / company</Label><Input required value={r.company_name} onChange={(e) => { const arr = [...f.references]; arr[idx] = { ...r, company_name: e.target.value }; setF({ ...f, references: arr }); }} data-testid={`ref-company-${idx}`} /></div>
                <div><Label>Company website (optional — becomes a backlink on your profile)</Label><Input type="url" value={r.company_website} onChange={(e) => { const arr = [...f.references]; arr[idx] = { ...r, company_website: e.target.value }; setF({ ...f, references: arr }); }} placeholder="https://example.com" data-testid={`ref-website-${idx}`} /></div>
                <div><Label>Referee name</Label><Input required value={r.referee_name} onChange={(e) => { const arr = [...f.references]; arr[idx] = { ...r, referee_name: e.target.value }; setF({ ...f, references: arr }); }} data-testid={`ref-name-${idx}`} /></div>
                <div><Label>Referee email</Label><Input required type="email" value={r.referee_email} onChange={(e) => { const arr = [...f.references]; arr[idx] = { ...r, referee_email: e.target.value.trim().toLowerCase() }; setF({ ...f, references: arr }); }} data-testid={`ref-email-${idx}`} /></div>
                <div><Label>Referee phone (optional)</Label><Input value={r.referee_phone} onChange={(e) => { const arr = [...f.references]; arr[idx] = { ...r, referee_phone: e.target.value }; setF({ ...f, references: arr }); }} data-testid={`ref-phone-${idx}`} /></div>
                <div><Label>Your role there</Label><Input value={r.role_held} onChange={(e) => { const arr = [...f.references]; arr[idx] = { ...r, role_held: e.target.value }; setF({ ...f, references: arr }); }} placeholder="e.g., CPO, Static Guard" data-testid={`ref-role-${idx}`} /></div>
                <div><Label>Period worked</Label><Input value={r.period} onChange={(e) => { const arr = [...f.references]; arr[idx] = { ...r, period: e.target.value }; setF({ ...f, references: arr }); }} placeholder="Mar 2022 – Jul 2024" data-testid={`ref-period-${idx}`} /></div>
                <div className="md:col-span-2"><Label>Nature of work (optional — shown to referee)</Label><Textarea rows={2} value={r.context} onChange={(e) => { const arr = [...f.references]; arr[idx] = { ...r, context: e.target.value }; setF({ ...f, references: arr }); }} placeholder="Brief description of what you did there — helps the referee remember." data-testid={`ref-context-${idx}`} /></div>
              </div>
            </div>
          ))}

          <button type="button" onClick={() => setF({ ...f, references: [...f.references, { company_name: "", company_website: "", referee_name: "", referee_email: "", referee_phone: "", role_held: "", period: "", context: "" }] })} className="text-sm text-brand font-manrope font-semibold" data-testid="ref-add">
            + Add another reference
          </button>
        </section>

        <label className="flex items-start gap-3 text-sm font-manrope">
          <Checkbox checked={f.agreement_accepted} onCheckedChange={(v) => setF({ ...f, agreement_accepted: !!v })} data-testid="reg-staff-agreement" />          <span>I agree to the SecuSource platform terms and confirm the information provided is accurate.</span>
        </label>

        <section>
          <Label className="font-manrope">Referral code (optional)</Label>
          <Input
            value={f.referral_code}
            onChange={(e) => setF({ ...f, referral_code: e.target.value.toUpperCase() })}
            placeholder="e.g., K7X4QP"
            className="uppercase"
            data-testid="reg-staff-referral"
          />
          <p className="text-xs text-white/60 mt-1 font-manrope">Referred by another verified pro? Enter their code — they get a 30-day priority boost when you&apos;re approved.</p>
        </section>

        {error && <div className="text-sm text-red-600 font-manrope">{error}</div>}

        <Button type="submit" disabled={busy} className="w-full bg-brand hover:bg-brand-hover text-white py-6 font-manrope font-semibold" data-testid={AUTH.registerStaffSubmit}>
          {busy ? "Submitting…" : "Register & Submit for Approval"}
        </Button>

        <div className="text-center text-sm text-white/70 font-manrope">
          Already have an account? <Link to="/login" className="text-brand font-semibold">Login</Link>
        </div>
      </form>
    </div>
  );
}

// Backwards-compat named export for any legacy imports
export const COUNTIES = IE_COUNTIES;
